import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Divider, Modal, Popover, Result, message } from 'antd'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import actions from '../../../../../../../../store/actions'

import createLayoutData from './layoutData'

import './style.scss'

const LayoutOptions = props => {
  const { t } = useTranslation()
  const [selectedLayout, setSelectedLayout] = useState({
    layout: null,
    isVideo: false
  })
  const [showLayoutModal, setShowLayoutModal] = useState({
    visible: false,
    layoutStyle: null,
    isVideo: false
  })

  const { currentPage, pageStructureData, lessonUpdate, isUpdating } = props
  const dispatch = useDispatch()

  const getCurrentPageTextData = (currentPageData = {}) => {
    const pageData = currentPageData.data

    if (pageData) {
      const totalElements = pageData.filter(
        item => item.display !== 'HEADING' && item.type === 'TEXT'
      )

      if (totalElements.length > 1) {
        const combinedText = totalElements
          .map(item => item.params.content)
          .join(' ')
        return combinedText
      }

      return (totalElements[0] && totalElements[0].params.content) || ''
    }

    return ''
  }

  const getCurrentPageImages = (currentPageData = {}) => {
    const pageData = currentPageData.data

    if (pageData) {
      const imageItem = pageData.filter(
        item => item.type === 'IMAGE' && item.display !== 'LOGO'
      )

      return imageItem.map(item => ({
        content: item.params.content,
        content_id: item.params.content_id
      }))
    }

    return []
  }

  const getCurrentPageVideos = (currentPageData = {}) => {
    const pageData = currentPageData.data

    if (pageData) {
      const videoItem = pageData.filter(item => item.type === 'VIDEO')

      return videoItem.map(item => ({
        content: item.params.content,
        content_id: item.params.content_id
      }))
    }

    return []
  }

  function splitParagraph(paragraph) {
    if (!paragraph) return ['', '']
    const sentences = paragraph.split(/(?<=\.|\?|\!)\s/)
    const midpoint = Math.ceil(sentences.length / 2)
    const firstHalf = sentences.slice(0, midpoint).join(' ') || ''
    const secondHalf = sentences.slice(midpoint).join(' ') || ''
    return [firstHalf, secondHalf]
  }

  useEffect(() => {
    const currentPageData = pageStructureData[currentPage - 1]
    if (!currentPageData || !selectedLayout.layout) return

    const existingHeading = currentPageData.data.find(
      item => item.display === 'HEADING'
    )

    const existingBackground = currentPageData.data.find(
      item =>
        item.type === 'BACKGROUND_IMAGE' || item.type === 'BACKGROUND_COLOR'
    )
    const textData = getCurrentPageTextData(currentPageData)
    const mediaData = selectedLayout.isVideo
      ? getCurrentPageVideos(currentPageData)
      : getCurrentPageImages(currentPageData)
    const logoData = getCurrentPageLogo(currentPageData)

    const layoutArray = createLayoutData(
      textData,
      mediaData,
      logoData,
      existingHeading && existingHeading.params.content,
      selectedLayout.isVideo
    )
    const selectedLayoutData = layoutArray.find(
      layout => layout.layoutStyle === selectedLayout.layout
    )

    if (!selectedLayoutData) return

    let updatedPageData = {
      ...currentPageData,
      ...selectedLayoutData.generateLayout()
    }

    if (existingBackground) {
      updatedPageData = {
        ...updatedPageData,
        data: [...updatedPageData.data, existingBackground]
      }
    }

    const updatedData = [...pageStructureData]
    updatedData[currentPage - 1] = updatedPageData

    const { handleLessonInput } = actions.lessonBuilder
    dispatch(handleLessonInput('pages_landscape', updatedData))
    lessonUpdate({ pages_landScape: updatedData })
  }, [selectedLayout])

  const handleSelectLayout = (layout, mediaType) => {
    if (isUpdating) return

    const currentPageData = pageStructureData[currentPage - 1]
    const pageHasScenario =
      currentPageData &&
      currentPageData.data.some(node => node.type === 'SCENARIO')

    if (pageHasScenario) {
      message.error(
        'You cannot change layout in a scenario page. Please try other pages or remove the scenario from the page first.',
        10
      )
      return
    }

    setShowLayoutModal({
      visible: true,
      layoutStyle: layout,
      isVideo: mediaType === 'VIDEO'
    })
  }

  const onConfirmLayoutChange = () => {
    setSelectedLayout({
      layout: showLayoutModal.layoutStyle,
      isVideo: showLayoutModal.isVideo
    })
    setShowLayoutModal(pre => ({
      ...pre,
      visible: false
    }))
  }

  const getCurrentPageLogo = (currentPageData = {}) => {
    const pageData = currentPageData.data

    if (pageData) {
      const imageItem = pageData.filter(
        item => item.display === 'LOGO' && item.type === 'IMAGE'
      )

      return (imageItem[0] && imageItem[0].params) || {}
    }

    return {}
  }

  const layoutData = createLayoutData()

  const PopoverContent = ({ layout }) => {
    const previewImages = layout.previewImages

    return (
      <div className="layout-img-container">
        {previewImages.map((previewImage, index) => (
          <img
            key={index}
            className="layout-img"
            src={previewImage.img}
            alt=""
            onClick={() =>
              handleSelectLayout(layout.layoutStyle, previewImage.type)
            }
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <div className="layout-options">
        {layoutData.map((layout, index) => (
          <PopoverWrapper
            key={index}
            content={<PopoverContent layout={layout} />}
            placement="right"
            trigger={['click']}
          >
            <img
              key={index}
              className={`${selectedLayout === layout.layoutStyle ? 'layout-selected' : ''
                } layout-img`}
              src={layout.img}
              alt=""
            />
          </PopoverWrapper>
        ))}
      </div>

      <Modal
        visible={showLayoutModal.visible}
        onCancel={() =>
          setShowLayoutModal({
            visible: false,
            layoutStyle: null,
            isVideo: false
          })
        }
        footer={null}
        centered
      >
        <Result
          status="warning"
          title={t('v4:warning')}
          subTitle={t('v4:change_layout_page')}
          extra={[
            <Button
              key="back"
              onClick={() =>
                setShowLayoutModal({
                  visible: false,
                  layoutStyle: null,
                  isVideo: false
                })
              }
            >
              {t('buttons:cancel')}
            </Button>,
            <Button type="primary" key="submit" onClick={onConfirmLayoutChange}>
              {t('v3:confirm_change')}
            </Button>
          ]}
        >
          <strong>{t('v4:change_layout_message')}</strong>
        </Result>
      </Modal>
    </>
  )
}

export default LayoutOptions

const PopoverContentWrapper = ({ content, onImageClick }) => {
  const handleImageClick = e => {
    if (e.target.tagName.toLowerCase() === 'img') {
      // Call the original click handler from the content first
      const originalOnClick = e.target.onclick
      if (originalOnClick) {
        originalOnClick.call(e.target, e)
      }
      // Then close the popover
      onImageClick()
    }
  }

  return <div onClick={handleImageClick}>{content}</div>
}

const PopoverWrapper = ({ children, content }) => {
  const [visible, setVisible] = useState(false)

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      if (visible) {
        setVisible(false)
      }
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [visible])

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        visible &&
        !event.target.closest('.ant-popover') &&
        !event.target.closest('.layout-img')
      ) {
        setVisible(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [visible])

  const handleVisibleChange = useCallback(newVisible => {
    setVisible(newVisible)
  }, [])

  const handleContentImageClick = useCallback(() => {
    setVisible(false)
  }, [])

  return (
    <Popover
      content={
        <PopoverContentWrapper
          content={content}
          onImageClick={handleContentImageClick}
        />
      }
      placement="right"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      trigger="click"
    >
      {children}
    </Popover>
  )
}
